// src/pages/SubscriptionPage.js

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../firebase/useAuth';
import { cancelSubscription } from '../../firebase/user';
import { useModal } from '../../context/ModalContext';
import { auth } from '../../firebaseConfig';

// --- Main Layout Components ---
import MainNavbar from '../../components/MainNavbar';
import MainFooter from '../../components/MainFooter';

// --- UI Components ---
import Spinner from '../../components/common/Spinner';
import { useToast } from '../../context/ToastContext';
import PayPalModal from '../../components/subscription/PayPalModal';
import CancelSubscriptionModal from '../../components/subscription/CancelSubscriptionModal';

import { PricingCards } from '../../components/subscription/PricingCards';
import { PremiumGlimpse } from '../../components/subscription/PremiumGlimpse';
import { FeatureComparisonTable } from '../../components/subscription/FeatureComparisonTable';

// --- View for New/Free Users ---
const PricingView = ({ handleSelectPlan }) => (
  <div className="max-w-7xl mx-auto py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
    <div className="text-center">
      <h1 className="text-3xl sm:text-4xl font-extrabold text-content-primary sm:text-5xl tracking-tight">Go Beyond N5. Unlock Your Full Potential.</h1>
      <p className="mt-4 text-lg sm:text-xl text-content-secondary max-w-4xl mx-auto">
        Upgrade to Premium to unlock access to thousands of sentences and words across all JLPT levels.
      </p>
    </div>

    {/* Present the premium options first */}
    <div className="mt-8 sm:mt-12 text-center">
      <h2 className="text-2xl sm:text-3xl font-extrabold text-content-primary tracking-tight">Choose Your Premium Plan</h2>
      <p className="mt-4 text-lg sm:text-xl text-content-secondary max-w-2xl mx-auto">Select the plan that works best for your learning journey.</p>
    </div>
    <PricingCards onSelectPlan={handleSelectPlan} showButtons />

    {/* Then show what the platform offers */}
    <PremiumGlimpse />

    {/* Feature comparison */}
    <FeatureComparisonTable onSelectPlan={handleSelectPlan} />
  </div>
);

// --- View for Existing Paid Subscribers ---
const ManageSubscriptionView = ({ subscription, onCancel, onUpgrade }) => (
  <div className="max-w-7xl mx-auto py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
    <div className="text-center mb-8 sm:mb-12">
      <h1 className="text-3xl sm:text-4xl font-extrabold text-content-primary sm:text-5xl tracking-tight">Your Subscription</h1>
    </div>

    {/* Current Subscription Card */}
    <div className="max-w-4xl mx-auto mb-12 sm:mb-16">
      <div
        className="relative overflow-hidden rounded-2xl backdrop-blur-sm border-2 border-primary p-6 sm:p-8"
        style={{
          background: 'var(--color-background-secondary)',
        }}
      >
        {/* Low opacity glassy bubbles - adjusted for mobile */}
        <div className="absolute inset-0 opacity-5">
          <div
            className="absolute rounded-full top-0 right-0 translate-x-4 sm:translate-x-8 -translate-y-6 sm:-translate-y-12 backdrop-blur-sm"
            style={{
              background: 'linear-gradient(135deg, var(--color-primary), var(--color-secondary))',
              width: '80px',
              height: '80px',
            }}
          />
          <div
            className="absolute rounded-full bottom-0 left-0 -translate-x-3 sm:-translate-x-6 translate-y-5 sm:translate-y-10 backdrop-blur-sm"
            style={{
              background: 'linear-gradient(45deg, var(--color-primary), var(--color-secondary))',
              width: '60px',
              height: '60px',
            }}
          />
        </div>

        <div className="relative z-10 flex flex-col sm:flex-row justify-between items-start gap-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-content-primary drop-shadow-sm">
              You are on the <span className="text-primary">{subscription.plan}</span> plan.
            </h2>
            {subscription.plan === 'Lifetime' ? (
              <p className="mt-2 text-base sm:text-lg text-content-secondary">Thank you for being a lifetime member! You have permanent access to all features.</p>
            ) : subscription.isCanceled ? (
              <p className="mt-2 text-base sm:text-lg text-red-500 font-medium">
                Your subscription is canceled and access will expire on{' '}
                <strong className="font-semibold">{new Date(subscription.dueDate).toLocaleDateString()}</strong>.
              </p>
            ) : (
              <p className="mt-2 text-base sm:text-lg text-content-secondary">
                Your plan renews on{' '}
                <strong className="font-semibold text-content-primary">{new Date(subscription.dueDate).toLocaleDateString()}</strong>.
              </p>
            )}
          </div>
        </div>

        {!subscription.isCanceled && subscription.plan !== 'Lifetime' && (
          <div className="relative z-10 mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-border flex justify-end">
            <button onClick={onCancel} className="text-xs sm:text-sm text-red-600 hover:text-red-500 font-medium transition-colors duration-200 hover:underline">
              Cancel Subscription & Switch to Free Plan
            </button>
          </div>
        )}
      </div>
    </div>

    {/* Show available plans for canceled users */}
    {subscription.isCanceled && (subscription.plan === 'Monthly' || subscription.plan === 'Yearly') && (
      <div className="text-center">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-content-primary tracking-tight">Available Plans</h2>
        <p className="mt-4 text-lg sm:text-xl text-content-secondary max-w-2xl mx-auto">
          Your current plan expires on <strong className="text-content-primary">{new Date(subscription.dueDate).toLocaleDateString()}</strong>. You
          can resubscribe to any plan after this date.
        </p>
        <PricingCards onSelectPlan={onUpgrade} showButtons={false} />
      </div>
    )}
  </div>
);

// --- Main Page Component ---
function SubscriptionPage() {
  const [subscription, setSubscription] = useState({ plan: null });
  const { showToast } = useToast();
  const [, setSelectedPlan] = useState(null);
const { showModal, hideModal } = useModal();

  const { userId, userLoading, subscriptionStatus } = useAuth();

  const updateSubscriptionFromContext = useCallback(() => {
    if (!subscriptionStatus.loading) {
      setSubscription({
        isSubscribed: subscriptionStatus.isSubscribed,
        plan: subscriptionStatus.plan || 'Free',
        dueDate: subscriptionStatus.dueDate,
        isCanceled: subscriptionStatus.isCanceled,
      });
    }
  }, [subscriptionStatus]);

  useEffect(() => {
    if (!userLoading && userId) updateSubscriptionFromContext();
  }, [userId, userLoading, updateSubscriptionFromContext]);

  const handleSelectPlan = (plan) => {
    if (auth.currentUser && !auth.currentUser.emailVerified) {
      showToast('Please verify your email before subscribing.', 'error');
      return;
    }
    setSelectedPlan(plan);
    showModal(<PayPalModal selectedPlan={plan} onSuccess={handlePayPalSuccess} onError={handlePayPalError} onClose={hideModal} />);
  };

  const handlePayPalSuccess = (plan) => {
    showToast(`Successfully subscribed to the ${plan} plan! Your subscription is now active.`, 'success');
    hideModal();
    // No need to manually refresh - useAuth context will update automatically
  };

  const handlePayPalError = (err) => {
    console.error('PayPal Error:', err);
    showToast('An error occurred with the payment. You have not been charged.', 'error');
    hideModal();
  };

  const handleOpenCancelModal = () => {
    showModal(<CancelSubscriptionModal onClose={hideModal} onConfirm={handleConfirmCancel} />);
  };

  const handleConfirmCancel = async () => {
    try {
      await cancelSubscription(userId);
      showToast('Your subscription will be canceled at the end of the billing period.', 'success');
    } catch (error) {
      console.error('Error canceling subscription:', error);
      showToast('Failed to cancel subscription. Please try again or contact support.', 'error');
    } finally {
      hideModal();
    }
  };

  const renderContent = () => {
    if (userLoading || subscriptionStatus.loading || !subscription.plan) {
      return (
        <div className="flex-grow flex items-center justify-center">
          <Spinner />
        </div>
      );
    }

    if (subscription.isSubscribed) {
      return <ManageSubscriptionView subscription={subscription} onCancel={handleOpenCancelModal} onUpgrade={handleSelectPlan} />;
    } else {
      return <PricingView handleSelectPlan={handleSelectPlan} />;
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-background-primary overflow-x-hidden">
      <MainNavbar />
      <main className="flex-grow flex flex-col overflow-x-hidden">{renderContent()}</main>
      <MainFooter />
    </div>
  );
}

export default SubscriptionPage;